import React, { useEffect, useState } from "react";

import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./ui/dialog";
import { ChevronDown, X, PlusCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import coin from "../assets/images/placeholder/crypto-colour.png";
import { useAllBanks } from "../hooks/query/queries";
const BankSelect = ({
  onSelect,
  onAddBank,
  isWithdraw,
  bankData,
  flagSelectedBank,
  parentSelectedBank,
  loadingExisting,
}) => {
  const [banks, setBanks] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBank, setSelectedBank] = useState(parentSelectedBank);
  const [open, setOpen] = useState(false);
  const { data: allBanks } = useAllBanks();

  useEffect(() => {
    if (parentSelectedBank) {
      setSelectedBank(parentSelectedBank);
    }
  }, [parentSelectedBank]);

  useEffect(() => {
    if (!isWithdraw) {
      if (allBanks) {
        setBanks(allBanks);
      }
    } else {
      if (bankData) {
        setBanks(bankData);
      }
    }
  }, [bankData, allBanks, isWithdraw]);

  const handleBankSelect = (bank) => {
    setSelectedBank(bank);
    if (onSelect) {
      onSelect(bank);
    }
    setOpen(false);
  };

  const filteredAssets = bankData
    ? bankData.filter((assets) => {
        const name = assets?.bankName?.toLowerCase() || "";
        const acc = assets?.accNumber?.toLowerCase() || "";
        const search = searchTerm?.toLowerCase() || "";
        return name.includes(search) || acc.includes(search);
      })
    : bankData;

  const filteredBanks = allBanks
    ? allBanks.filter((bank) => {
        const name = bank?.name?.toLowerCase() || "";
        const branchCode =
          bank?.universalBranchCode?.toLowerCase() ||
          bank?.branchCode?.toLowerCase() ||
          "";
        const country = bank?.country?.toLowerCase() || "";
        const search = searchTerm?.toLowerCase() || "";
        return (
          name.includes(search) ||
          branchCode.includes(search) ||
          country.includes(search)
        );
      })
    : allBanks;

  return (
    <>
      {!isWithdraw && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            {loadingExisting ? (
              <div className="w-full flex items-center h-[56px] justify-between py-4  px-4 rounded-2xl border border-gray-300 bg-muted/50">
                <div className="flex items-center gap-3 ">
                  <Skeleton className="w-10 h-10 rounded-full bg-gray-300" />

                  <div className="flex flex-col gap-1">
                    <Skeleton className="w-10 h-5 rounded-full bg-gray-300" />
                  </div>
                </div>
                <Skeleton className="w-6 h-6 rounded-full bg-gray-300" />
              </div>
            ) : (
              <div
                className={`border md:rounded-lg rounded-b-2xl px-4 py-3 h-[56px] flex items-center justify-between cursor-pointer min-w-[220px] ${
                  flagSelectedBank ? "border-2 border-rose-500" : ""
                }`}
              >
                <div className="flex items-center gap-2">
                  <img
                    src={selectedBank?.iconUrl || coin}
                    alt={selectedBank?.name || "Bank Icon"}
                    className="w-6 h-6"
                  />
                  <div className="flex flex-col leading-tight">
                    <span className="text-[14px] font-semibold text-[#6D6584]">
                      {selectedBank?.name || "Select a bank"}
                    </span>
                    {selectedBank && (
                      <span className="text-[14px] font-normal text-[#6D6584]">
                        Branch Code {selectedBank?.universalBranchCode}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronDown size={16} className="text-[#6D6584]" />
              </div>
            )}
          </DialogTrigger>

          <DialogContent className="max-w-sm rounded-2xl p-6">
            <DialogClose asChild>
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                className="w-10 h-10 absolute top-4 right-4 text-[#6D6584] hover:text-[#6D6584] rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
              >
                <X size={18} className="text-gray-700" />
              </Button>
            </DialogClose>

            <DialogHeader>
              <DialogTitle className="text-[20px] font-semibold text-[#6D6584]">
                Select a bank account
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <input
                type="text"
                placeholder="Search addresses..."
                className="w-full border rounded-lg px-3 py-2 h-[56px]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="mt-4 flex flex-col space-y-3 max-h-72 overflow-y-auto">
              {banks ? (
                filteredBanks?.length > 0 ? (
                  filteredBanks.map((bank) => {
                    return (
                      <button
                        key={bank._id || bank.bankId}
                        onClick={() => {
                          handleBankSelect(bank);
                        }}
                        disabled={false}
                        className="flex items-center gap-3 px-3 py-3 border rounded-lg border-transparent bg-gray-50 hover:border-rose-200 h-[56px] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <img
                          src={bank.iconUrl}
                          alt={bank.name}
                          className="w-6 h-6"
                        />
                        <div className="flex flex-col text-left">
                          <span>{bank.name}</span>
                          <span className="text-xs text-gray-500">
                            {bank.universalBranchCode || bank.branchCode
                              ? `Branch Code ${bank.universalBranchCode || bank.branchCode}`
                              : bank.country || ""}
                          </span>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <p className="text-center text-gray-500 py-4">
                    No banks found
                  </p>
                )
              ) : (
                <p>No banks</p>
              )}
            </div>
            <div className="pt-2 border-t border-gray-200 text-center">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  if (onAddBank) {
                    onAddBank();
                  }
                }}
                className="inline-flex items-center gap-2 text-sm text-rose-600 font-medium hover:underline"
              >
                <PlusCircle size={16} />
                Add another bank account
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {isWithdraw && (
        <>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              {loadingExisting ? (
                <div className="w-full flex items-center justify-between py-4  px-4 rounded-2xl border border-gray-300 bg-muted/50">
                  <div className="flex items-center gap-3 ">
                    <Skeleton className="w-10 h-10 rounded-full bg-gray-300" />

                    <div className="flex flex-col gap-1">
                      <Skeleton className="w-10 h-5 rounded-full bg-gray-300" />
                    </div>
                  </div>
                  <Skeleton className="w-6 h-6 rounded-full bg-gray-300" />
                </div>
              ) : (
                <div
                  className={`border h-[70px] md:h-[88px] md:rounded-lg rounded-t-2xl  px-6 py-3 flex items-center justify-between cursor-pointer min-w-[220px] ${
                    flagSelectedBank ? "border-2 border-rose-500" : ""
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <img
                      src={selectedBank?.bankIconUrl?.trim?.() || coin}
                      alt={selectedBank?.bankName || "Bank Icon"}
                      className="w-[40px] h-[40px]"
                    />
                    <div className="flex flex-col gap-1 leading-tight">
                      <span
                        className={`text-[16px] font-semibold ${
                          flagSelectedBank ? "text-rose-500" : "text-[#423E51]"
                        }`}
                      >
                        {selectedBank?.bankName || "Select a bank"}
                      </span>

                      <span
                        className={`text-[14px] font-normal ${
                          flagSelectedBank ? "text-rose-500" : "text-[#6D6584]"
                        }`}
                      >
                        Acc: {selectedBank?.accNumber}
                      </span>
                    </div>
                  </div>
                  <ChevronDown
                    size={16}
                    className={
                      flagSelectedBank ? "text-rose-500" : "text-[#6D6584]"
                    }
                  />
                </div>
              )}
            </DialogTrigger>

            <DialogContent className="max-w-sm rounded-2xl p-6">
              <DialogClose asChild>
                <Button
                  variant="ghost"
                  onClick={() => setOpen(false)}
                  className="w-10 h-10 absolute top-4 right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
                >
                  <X size={18} className="text-gray-700" />
                </Button>
              </DialogClose>

              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text-gray-800">
                  Select a bank account
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                <input
                  type="text"
                  placeholder="Search addresses..."
                  className="w-full border rounded-lg px-3 py-2"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="mt-4 flex flex-col space-y-3 max-h-72 overflow-y-auto">
                {banks && filteredAssets && filteredAssets.length > 0 ? (
                  filteredAssets.map((bank) => {
                    return (
                      <button
                        key={bank._id}
                        onClick={() => {
                          handleBankSelect(bank);
                        }}
                        disabled={false}
                        className="flex items-center gap-3 px-3 py-3 border rounded-lg border-transparent bg-gray-50 hover:border-rose-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <img
                          src={bank.bankIconUrl || coin}
                          alt={bank.bankName || "Bank"}
                          className="w-6 h-6"
                        />
                        <div className="flex flex-col text-left">
                          <span>{bank.bankName}</span>
                          <span className="text-xs text-gray-500">
                            Acc: {bank.accNumber}
                          </span>
                        </div>
                      </button>
                    );
                  })
                ) : banks && filteredAssets && filteredAssets.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">
                    No banks found
                  </p>
                ) : null}
              </div>
              <div className="pt-2 border-t border-gray-200 text-center">
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    if (onAddBank) {
                      onAddBank();
                    }
                  }}
                  className="inline-flex items-center gap-2 text-sm text-rose-600 font-medium hover:underline"
                >
                  <PlusCircle size={16} />
                  Add another bank account
                </button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </>
  );
};

export default BankSelect;
